import React, { FC, useRef, useState, useEffect } from 'react';
import Button from 'src/components/Button';
import translate from '@translate';
import { useDispatch, useSelector } from 'react-redux';
import { RootStoreType } from 'src/types/store';
import { useNavigate } from 'react-router-dom';
import TextField from 'src/components/TextField';
import { ReceivingType } from 'src/types/receiving';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import FormBlock from 'src/components/FormBlock';
import NewProductBlock from './NewProductBlock';
import { sizes } from 'src/themes/default';
import ProductsTable from './ProductsTable';
import { addMessage } from 'src/resources/alertMessages/reducer';
import { updateReceivingDetailFetch } from 'src/resources/receivingDetail/reducer';
import Dialog from 'src/components/Dialog';
import AutocompleteTextField from 'src/components/AutocompleteTextField';
import { FormErrorsType, OnChangeType } from '../../../types/global';

export const EDIT_KEYS = ['NOTE', 'PRODUCTS'];

export type EditKeysType = 'NOTE' | 'PRODUCTS';

export interface ConfirmReceivingPropsType {
    idReceiving: string;
}

const initData = {};

const ConfirmReceiving: FC<ConfirmReceivingPropsType> = (props) => {
    const { idReceiving } = props;
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const productsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.receivingDetail.data);

    const [editData, setEditData] = useState<ReceivingType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});

    useEffect(() => {
        if (data) {
            if (data.STATUS !== 'received') {
                handleCancel();
            } else {
                setEditData({
                    ...data,
                    PRODUCTS: (data.PRODUCTS || []).filter((product) => product.LINE_STATE !== 'D'),
                });
            }
        }
    }, [data]);

    const handleCancel: any = () => {
        navigate(`/receivings/${idReceiving}/`);
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProduct: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleSave = () => {
        const newFormErrors = checkFormValidity(productsFormRef);

        if (!Object.keys(newFormErrors).length) {
            const formatData = {
                ...editData,
                PRODUCTS: editData.PRODUCTS?.map((product) => {
                    const newProduct = { ...product };
                    if (newProduct.RECEIVINGS_PRODUCTS_ID && newProduct.RECEIVINGS_PRODUCTS_ID < 0) {
                        delete newProduct.RECEIVINGS_PRODUCTS_ID;
                    }
                    return newProduct;
                }),
                STATUS: 'confirmed',
            };
            dispatch(updateReceivingDetailFetch({ idReceiving, data: formatData }));
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText'),
                })
            );
        }

        setEditErrors(newFormErrors);
    };

    return (
        <Dialog
            maxWidth="xl"
            title={translate('receivingDetailConfirm.title', { idReceiving })}
            dontClose
            actions={
                <>
                    <Button variant="contained" onClick={handleSave}>
                        {translate('receivingDetailConfirm.action.confirm')}
                    </Button>
                    <Button variant="outlined" onClick={handleCancel}>
                        {translate('receivingDetailConfirm.action.cancel')}
                    </Button>
                </>
            }
        >
            <>
                <FormBlock sx={{ marginBottom: sizes.blocks_top_MG }}>
                    <AutocompleteTextField
                        value={editData.NOTE}
                        error={editErrors.NOTE}
                        onChange={(value, error) => onChange('NOTE', value, error)}
                        name="NOTE"
                        label={translate('receivingDetailConfirm.note')}
                        fullWidth
                        multiline
                        autoComplete="RECEIVING_NOTE"
                    />
                </FormBlock>
                <FormBlock>
                    <NewProductBlock
                        data={editData.PRODUCTS || []}
                        onChange={(value: any) => onChange('PRODUCTS', value, false)}
                    />
                </FormBlock>
                <form ref={productsFormRef}>
                    {editData.PRODUCTS && (
                        <ProductsTable
                            data={editData.PRODUCTS}
                            onChange={(value: any, error: boolean, errorKey?: string) =>
                                onChangeProduct('PRODUCTS', value, error, errorKey)
                            }
                            editErrors={editErrors}
                        />
                    )}
                </form>
            </>
        </Dialog>
    );
};

export default ConfirmReceiving;
